import React, { memo, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { fetchGet } from "app/utils/request";
import gridIcon from "app/images/gg-menu-grid.svg";
import { loadGgMenuScript, showGgMenu } from "app/library/gg-menu";
import styles from "./styles.module.scss";

// Note: the request layer deep-camelizes responses, so the backend's
// `script_url` / `menu_data` arrive here as `scriptUrl` / `menuData`.
// `menuData` is a JSON string (kept opaque so its snake_case contents survive
// camelization) that we parse back into the raw payload gg_menu.js expects.
interface GgMenuResponse {
  scriptUrl: string | null;
  menuData: string | null;
}

/**
 * Golf Genius Cross Product Navigation button (9-dot app switcher).
 *
 * Rendered only when the backend indicates CPN is available for the current user
 * (`golfgeniusCpnAvailable`). On mount it fetches the per-user menu data + script
 * URL from the backend and loads the IdP menu script; clicking opens the Golf
 * Genius menu.
 */
const GgMenuButton = () => {
  const user = useSelector(
    (state: RootState) => state.user.self,
    shallowEqual,
  );
  // Backend-computed: true only when linked to Golf Genius AND the tokens needed
  // for CPN to work are stored. Gating on this (not just golfgeniusId) avoids
  // showing a button that can't actually open the menu.
  const cpnAvailable = user?.golfgeniusCpnAvailable;

  const [menuData, setMenuData] = useState<unknown>(null);
  const readyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      try {
        const res = (await fetchGet<GgMenuResponse>(
          "gg_menu",
          {},
          { version: 1 },
        )) as GgMenuResponse;

        if (cancelled || !res || !res.scriptUrl) {
          return;
        }

        if (res.menuData) {
          try {
            setMenuData(JSON.parse(res.menuData));
          } catch {
            // leave menuData null; the menu just won't open
          }
        }
        await loadGgMenuScript(res.scriptUrl);

        if (!cancelled) {
          readyRef.current = true;
        }
      } catch {
        // Non-fatal: if the data/script can't load the button just won't open.
      }
    };

    if (cpnAvailable) {
      setup();
    }

    return () => {
      cancelled = true;
    };
  }, [cpnAvailable]);

  if (!cpnAvailable) {
    return null;
  }

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    // Sits inside the sidebar logo row, which navigates home on click — keep
    // clicks on this button from bubbling up to that handler.
    event.stopPropagation();
    if (readyRef.current) {
      showGgMenu(menuData);
    }
  };

  return (
    <a
      href="#"
      className={styles.gg_menu_btn}
      onClick={handleClick}
      aria-label="Golf Genius apps"
    >
      <img src={gridIcon} alt="Golf Genius apps" />
    </a>
  );
};

export default memo(GgMenuButton);
